// ------------------------------------------------------------------------------
// ContractUploader
// author: mudas
// created: 2025/3/18
// ------------------------------------------------------------------------------

import React, { FC, ReactElement } from 'react';
import { useControllableValue } from 'ahooks';
import { useComputed } from 'common/hooks';
import { matchMimeType, normalizeMimeType } from 'common/utils';

import { DraggerUploadFile, UploadFileItem } from 'common/types';
import UploadFileDragger, { UploadFileDraggerProps } from './UploadFileDragger';

import { DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { App, Button, Icon, Tooltip, Typography, Upload, UploadFile } from '@fuxi/eevee-ui';

import cx from './ArtWorksUploader.module.css';

export interface IProps extends Omit<UploadFileDraggerProps, 'value' | 'onChange'> {
  // 限制上传数量（默认：1）
  limit?: number;

  // 限制上传文件大小（默认：100MB，单位：MB）
  fileSize?: number;

  // 限制上传文件类型，支持多种格式： txt、.txt、text/plain
  extensions?: string[];

  // 是否允许同时上传多个文件（默认：false）
  multiple?: boolean;

  value?: UploadFileItem[];
  onChange?: (value?: UploadFileItem[]) => void;
}

const MaxFileSize = 100; // 最大上传单个文件大小
const MaxFileAmount = 1; // 最大上传文件数量

const ContractUploader: FC<IProps> = (props) => {
  const {
    limit = MaxFileAmount,
    fileSize = MaxFileSize,
    extensions,
    multiple,
    value,
    onChange,
    ...draggerProps
  } = props;

  const { message } = App.useApp();
  const [contractFiles, setContractFiles] = useControllableValue<UploadFileItem[]>(props, { defaultValue: [] });

  const mimeTypes = useComputed(() => {
    return Array.isArray(extensions) ? normalizeMimeType(extensions).join(',') : '';
  }, [extensions]);

  const beforeUpload = async (file: UploadFile, fileList: UploadFile[]) => {
    if (!file) {
      return Upload.LIST_IGNORE;
    }

    // 限制文件数量
    if ((contractFiles?.length ?? 0) + (fileList?.length ?? 0) > limit) {
      message.error(`最多上传 ${limit} 个文件`);
      return Upload.LIST_IGNORE;
    }

    if (Array.isArray(extensions) && extensions.length > 0 && !matchMimeType(file.name, extensions)) {
      message.error(`不支持上传该格式文件`);
      return Upload.LIST_IGNORE;
    }

    if ((file.size ?? 0) / (1024 * 1024) > fileSize) {
      message.error(`请上传 ${fileSize}MB 以内的文件`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  // --------------------------------------------------------------------------
  //
  // Event handlers
  //
  // --------------------------------------------------------------------------

  const handleChange = (file: DraggerUploadFile) => {
    const { uid, name, status, percent, response } = file;

    setContractFiles((files) => {
      // 错误的配置值可能导致 files 异常（如 DSL 中将默认值置为 ""）
      files = Array.isArray(files) ? files : [];

      // uid 与 url.fileId 皆可做唯一检测
      const target = files?.find((item) => item.uid === uid);

      // 删除
      if (target && status === 'removed') return files.filter((item) => item.uid !== uid);

      if (target) {
        target.percent = percent ?? 0;
        target.status = status;

        if (status === 'done') {
          const { url, previewUrl, type, illegal } = response!;
          target.type = type;
          target.thumbUrl = previewUrl;
          target.url = url;
          target.illegal = !!illegal;
        }
      } else {
        files.push({ uid, name, percent: 0, status, illegal: false });
      }

      return [...files];
    });
  };

  const itemRender = (
    originNode: ReactElement,
    file: DraggerUploadFile,
    fileList: object[],
    { remove },
    // actions: { download: function; preview: function; remove: function },
  ) => {
    const { name, status, response } = file;

    return (
      <div className={cx('file-item')}>
        <div className="flex items-center gap-2">
          <Icon size={16} name="doc-outlined" className={`color-primary ${status === 'error' ? 'color-error!' : ''}`} />

          <Typography.Link ellipsis className={`flex-1 min-w-0 ${response?.illegal ? 'color-error!' : ''}`}>
            {name}
          </Typography.Link>

          {response?.illegal && (
            <Tooltip title="图片存在违禁内容，请重新上传">
              <ExclamationCircleOutlined className="color-error" />
            </Tooltip>
          )}
        </div>

        <Button
          type="text"
          size="small"
          className={cx('icon-delete', status)}
          icon={status === 'uploading' ? <LoadingOutlined /> : <DeleteOutlined />}
          onClick={remove}
          disabled={status === 'uploading'}
        />
      </div>
    );
  };

  return (
    <div className="ContractUploader">
      <UploadFileDragger
        className={cx('works-uploader-wrapper')}
        listType="text"
        accept={mimeTypes}
        showUploadList
        multiple={multiple}
        onChange={handleChange}
        itemRender={itemRender}
        beforeUpload={beforeUpload}
        namespace="user.contract"
        {...draggerProps}>
        <div className="flex gap-3">
          <Icon size={40} name="doc-outlined" className="text-color-secondary" />

          <div className="text-align-left">
            <div>点击或拖拽上传文件</div>

            <Typography.Text type="secondary" className="font-size-xs">
              仅支持上传一个100M以内的文件，格式不限
            </Typography.Text>
          </div>
        </div>
      </UploadFileDragger>
    </div>
  );
};

export default ContractUploader;
